import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { CtaLink } from "@/components/marketing/CtaLink";
import { ArrowRight, Send, Sparkles, Check, type LucideIcon } from "lucide-react";
import { SiteLayout } from "./SiteLayout";
import { SiteBreadcrumbs } from "./SiteBreadcrumbs";
import { AiGeneratorFlow, type FlowConfig } from "./AiGeneratorFlow";

export type AiGeneratorSection = { icon: LucideIcon; title: string; desc: string };
export type AiGeneratorFaq = { q: string; a: string };

export type AiGeneratorPageProps = {
  eyebrow: string;
  title: string;
  italic: string;
  description: string;
  headline: string; // e.g. "What kind of contract are we drafting today?"
  placeholders: string[]; // typing autofill examples
  sections: AiGeneratorSection[]; // what we can do
  capabilities: string[];
  howItWorks: { title: string; desc: string }[];
  faq: AiGeneratorFaq[];
  // Multi-step wizard → loader → ready modal
  flow: FlowConfig;
  linkedFromHref?: string; // back link to the platform feature page
  linkedFromLabel?: string;
  outroCta?: { to: string; label: string };
};

export function AiGeneratorPage(p: AiGeneratorPageProps) {
  const [query, setQuery] = useState("");
  const [flowOpen, setFlowOpen] = useState(false);
  const placeholder = useTypingPlaceholder(p.placeholders, !query && !flowOpen);

  const submit = () => {
    const q = (query || p.placeholders[0]).trim();
    if (!q) return;
    if (!query) setQuery(q);
    setFlowOpen(true);
  };

  const useExample = (ex: string) => {
    setQuery(ex);
  };

  return (
    <SiteLayout>
      {/* HERO with AI gradient bg + input */}
      <section className="relative overflow-hidden">
        <div aria-hidden className="ai-bg-gradient pointer-events-none absolute inset-0" />
        <div className="relative mx-auto max-w-5xl px-4 pb-16 pt-12 md:px-6 md:pt-16">
          <SiteBreadcrumbs className="mb-6" currentName={p.eyebrow} />

          {p.linkedFromHref && p.linkedFromLabel && (
            <Link
              to={p.linkedFromHref}
              className="mb-6 inline-flex items-center gap-2 rounded-full bg-card/70 px-3 py-1.5 text-xs font-semibold backdrop-blur ring-1 ring-foreground/10 hover:bg-card"
            >
              <span className="rounded-full bg-foreground px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-background">
                Part of
              </span>
              <span>{p.linkedFromLabel}</span>
              <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          )}

          <h1 className="font-display text-4xl leading-[1.05] md:text-6xl">
            {p.title} <em className="italic">{p.italic}</em>
          </h1>
          <p className="mt-5 max-w-2xl text-base text-muted-foreground md:text-lg">{p.description}</p>

          {/* Generator box */}
          <div className="mt-10">
            <p className="mb-3 text-sm font-semibold text-foreground/80">{p.headline}</p>
            <div className="ai-gradient-border ai-gradient-border-thick !rounded-2xl shadow-card">
              <div className="rounded-2xl bg-card p-3 sm:p-4">
                <textarea
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      submit();
                    }
                  }}
                  placeholder={placeholder}
                  rows={3}
                  className="block w-full resize-none border-0 bg-transparent px-2 py-2 text-base text-foreground placeholder:text-muted-foreground focus:outline-none disabled:opacity-60"
                  style={{ fontSize: 16 }}
                />
                <div className="flex items-center justify-between gap-3 pt-2">
                  <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    <Sparkles className="h-3.5 w-3.5" />
                    Banana AI · Demo
                  </span>
                  <button
                    type="button"
                    onClick={submit}
                    className="inline-flex items-center gap-2 rounded-xl bg-foreground px-4 py-2.5 text-sm font-bold text-background transition hover:opacity-90"
                  >
                    Generate
                    <Send className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>

            {/* Example chips */}
            <div className="mt-4 flex flex-wrap gap-2">
              {p.placeholders.slice(0, 4).map((ex) => (
                <button
                  key={ex}
                  type="button"
                  onClick={() => useExample(ex)}
                  className="rounded-full bg-card px-3 py-1.5 text-xs font-semibold text-foreground/80 ring-1 ring-foreground/10 transition hover:bg-muted"
                >
                  {ex.length > 64 ? ex.slice(0, 64) + "…" : ex}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      <AiGeneratorFlow
        open={flowOpen}
        onClose={() => setFlowOpen(false)}
        config={p.flow}
        prompt={query || p.placeholders[0]}
      />

      {/* WHAT IT DOES — 3 columns */}
      <section className="bg-background">
        <div className="mx-auto max-w-7xl px-4 py-20 md:px-6">
          <div className="mx-auto mb-12 max-w-2xl text-center">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">What it does</p>
            <h2 className="mt-2 font-display text-3xl leading-[1.1] md:text-[2.5rem]">
              Powered by your <em className="italic">Banana workspace.</em>
            </h2>
          </div>
          <div className="grid gap-4 md:grid-cols-3">
            {p.sections.map((s) => {
              const Icon = s.icon;
              return (
                <div key={s.title} className="rounded-2xl bg-muted p-6">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-card">
                    <Icon className="h-5 w-5" />
                  </span>
                  <h3 className="mt-5 text-lg font-bold">{s.title}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{s.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-muted">
        <div className="mx-auto max-w-7xl px-4 py-20 md:px-6">
          <div className="mx-auto max-w-2xl text-center">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">How it works</p>
            <h2 className="mt-2 font-display text-3xl leading-[1.1] md:text-[2.5rem]">
              From prompt to result in <em className="italic">seconds.</em>
            </h2>
          </div>
          <ol className="mt-12 grid gap-4 md:grid-cols-4">
            {p.howItWorks.map((s, i) => (
              <li key={s.title} className="relative rounded-2xl bg-card p-6">
                <span className="absolute -top-3 left-6 inline-flex h-7 items-center justify-center rounded-full bg-foreground px-3 text-[11px] font-bold text-background">
                  Step {i + 1}
                </span>
                <h3 className="mt-3 text-base font-bold">{s.title}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{s.desc}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* CAPABILITIES */}
      <section className="bg-background">
        <div className="mx-auto max-w-7xl px-4 py-20 md:px-6">
          <div className="grid gap-12 md:grid-cols-2">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Capabilities</p>
              <h2 className="mt-2 font-display text-3xl leading-[1.1] md:text-[2.5rem]">
                Built for <em className="italic">real workflows.</em>
              </h2>
              <p className="mt-5 max-w-md text-base leading-relaxed text-muted-foreground">
                Free to try in the browser. Sign in to save, edit and send anything Banana generates for you.
              </p>
              <Link to="/pricing" className="mt-6 inline-flex items-center gap-1 text-sm font-bold underline-offset-4 hover:underline">
                Compare plans <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
            <ul className="grid gap-3 self-center sm:grid-cols-2">
              {p.capabilities.map((c) => (
                <li key={c} className="flex items-start gap-2 rounded-xl bg-muted p-4 text-sm">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-foreground" />
                  <span className="font-semibold">{c}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-muted">
        <div className="mx-auto max-w-4xl px-4 py-24 md:px-6">
          <div className="mb-10 text-center">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">FAQ</p>
            <h2 className="mt-2 font-display text-3xl leading-[1.1] md:text-[2.5rem]">
              Questions, <em className="italic">answered.</em>
            </h2>
          </div>
          <div className="divide-y divide-foreground/10 rounded-2xl bg-card">
            {p.faq.map((f) => (
              <details key={f.q} className="group p-5">
                <summary className="flex cursor-pointer items-center justify-between gap-4 text-sm font-bold">
                  {f.q}
                  <ArrowRight className="h-4 w-4 shrink-0 transition group-open:rotate-90" />
                </summary>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* OUTRO */}
      <section className="bg-background">
        <div className="mx-auto max-w-7xl px-4 pb-24 pt-4 md:px-6">
          <div className="relative overflow-hidden rounded-3xl bg-foreground px-8 py-16 text-center text-background md:p-20">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,oklch(0.96_0.21_117/0.15),transparent_50%)]" aria-hidden />
            <Sparkles className="mx-auto h-6 w-6 text-brand-lime" />
            <h2 className="mx-auto mt-5 max-w-2xl font-display text-3xl leading-[1.1] md:text-5xl">
              Try it free. <em className="italic text-brand-lime">No card required.</em>
            </h2>
            <p className="mx-auto mt-5 max-w-lg text-base text-background/70">
              Open the generator above and run as many drafts as you want. Save and send from your Banana workspace.
            </p>
            <div className="relative mt-8 flex flex-wrap items-center justify-center gap-3">
              <CtaLink to={p.outroCta?.to ?? "/signup"} className="inline-flex items-center gap-2 rounded-xl bg-brand-lime px-6 py-3 text-sm font-bold text-brand-lime-foreground">
                {p.outroCta?.label ?? "Get started — free"}
              </CtaLink>
              <Link to="/pricing" className="inline-flex items-center gap-2 rounded-xl border border-background/20 px-6 py-3 text-sm font-bold">
                See pricing
              </Link>
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}

function useTypingPlaceholder(messages: string[], active: boolean) {
  const [text, setText] = useState("");
  useEffect(() => {
    if (!active) { setText(""); return; }
    let cancelled = false;
    let i = 0;
    async function run() {
      while (!cancelled) {
        const full = messages[i % messages.length];
        for (let c = 1; c <= full.length && !cancelled; c++) {
          setText(full.slice(0, c));
          await wait(28);
        }
        await wait(1600);
        for (let c = full.length; c >= 0 && !cancelled; c--) {
          setText(full.slice(0, c));
          await wait(14);
        }
        await wait(300);
        i++;
      }
    }
    run();
    return () => { cancelled = true; };
  }, [messages, active]);
  return text || " ";
}
function wait(ms: number) { return new Promise<void>((r) => setTimeout(r, ms)); }

/* ===== Small banner used at top of platform feature pages to point to AI tools ===== */
export function AiToolBanner({ to, label, sublabel }: { to: string; label: string; sublabel?: string }) {
  return (
    <div className="bg-background">
      <div className="mx-auto max-w-7xl px-4 pt-6 md:px-6">
        <Link
          to={to}
          className="ai-gradient-border block !rounded-2xl"
        >
          <div className="flex items-center justify-between gap-3 rounded-2xl bg-card px-4 py-3 sm:px-5">
            <div className="flex min-w-0 items-center gap-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[linear-gradient(135deg,#6366f1_0%,#a855f7_50%,#ec4899_100%)] text-white shadow-[0_6px_16px_-4px_rgba(168,85,247,0.55)]">
                <Sparkles className="h-4 w-4" />
              </span>
              <div className="min-w-0">
                <p className="truncate text-sm font-bold">{label}</p>
                {sublabel && <p className="truncate text-xs text-muted-foreground">{sublabel}</p>}
              </div>
            </div>
            <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-foreground px-3 py-1.5 text-xs font-bold text-background">
              Try it
              <ArrowRight className="h-3.5 w-3.5" />
            </span>
          </div>
        </Link>
      </div>
    </div>
  );
}